import {
  Component,
  ChangeDetectorRef,
  AfterViewInit,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { map, catchError, take } from "rxjs/operators";
import { forkJoin, of } from "rxjs";
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  SocialPlatformI,
  ContentSiteI,
} from "../../../interfaces/core.interface";
import { CoreService } from "../../../services/core.service";
import { SocialAiFollowerGrowthChartComponent } from "../social-ai-follower-growth-chart/social-ai-follower-growth-chart.component";
import { SocialAiTrendsChartComponent } from "../social-ai-trends-chart/social-ai-trends-chart.component";
import { SocialAiFormatBreakdownChartComponent } from "../social-ai-format-breakdown-chart/social-ai-format-breakdown-chart.component";
import { SocialAiAlertsChartComponent } from "../social-ai-alerts-chart/social-ai-alerts-chart.component";
import {
  NgApexchartsModule,
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexDataLabels,
  ApexStroke,
  ApexGrid,
  ApexYAxis,
  ApexTooltip,
  ApexTitleSubtitle,
  ApexLegend,
} from "ng-apexcharts";
import { Router } from "@angular/router";

export type ChartOptions = Partial<{
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  dataLabels: ApexDataLabels;
  grid: ApexGrid;
  stroke: ApexStroke;
  tooltip: ApexTooltip;
  legend: ApexLegend;
  title: ApexTitleSubtitle;
}>;

// Minimal Post shape for the sidebar list
type Post = {
  id: number;
  rss_title: string;
  domain_name?: string;
  image_url: string;
  post_status?: string;
  enable_repeat?: number;
  parent_post_id?: number;
  tag?: string;
  social_platform_title?: string;
  scheduled_for?: string | Date;
  rss_description?: string;
};

@Component({
  selector: "app-newdash",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgApexchartsModule,
    SocialAiFollowerGrowthChartComponent,
    SocialAiTrendsChartComponent,
    SocialAiFormatBreakdownChartComponent,
    SocialAiAlertsChartComponent,
     MatTooltipModule,
  ],
  templateUrl: "./newdash.component.html",
  styleUrl: "./newdash.component.scss",
})
export class NewdashComponent implements AfterViewInit {
  public chartOptions6: ChartOptions = this.createChartOptions([], []);
  activeView: string = "followers";
  isLoaded: boolean = false;
  isMetricsLoaded = false;
  pages: any[] = [];
  sites: ContentSiteI[] = [];
  platforms: SocialPlatformI[] = [];
  filteredPlatforms: SocialPlatformI[] = [];
  selectedSiteId: number | null = null;
  selectedPlatformId: number | null = null;
  selectedPeriod: string = "days_28";
  companyID: number | null = null;

  // Sidebar posts
  maxSidebarCards = 3;
  posts: Post[] = [];
  naPlaceholder = "./assets/images/na.png";

  // match the key used in GridBasedLayoutComponent
  private readonly LATEST_POSTS_KEY = 'latestPosts';

  metrics = {
    reach: { value: 0, diff: 0 },
    accountImpressions: { value: 0, diff: 0 },
    profileImpressions: { value: 0, diff: 0 },
  };

  lastChartValue: number | null = null;
  diffChartValue: number | null = null;

  constructor(
    private coreService: CoreService,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {}

  /** ===============================
   *  LIFECYCLE
   *  =============================== */
  ngAfterViewInit(): void {
    this.companyID = this.coreService.user?.companyID || 0;

    forkJoin({
      sites: this.loadSites(),
      platforms: this.loadPlatforms(),
    }).subscribe(({ sites, platforms }) => {
      this.sites = sites as ContentSiteI[];
      this.platforms = platforms as SocialPlatformI[];

      if (this.sites.length > 0) {
        this.selectedSiteId = this.sites[0].ID;
      }

      this.filteredPlatforms = this.platforms.filter(
        (p) =>
          p.site_id === this.selectedSiteId &&
          (p.platform.toLowerCase().includes("facebook") ||
            p.platform.toLowerCase().includes("instagram"))
      );

      const fbPlatform = this.filteredPlatforms.find((p) =>
        p.platform.toLowerCase().includes("facebook")
      );
      this.selectedPlatformId = fbPlatform
        ? fbPlatform.id
        : this.filteredPlatforms[0]?.id || null;

      const selectedPlatform = fbPlatform || this.filteredPlatforms[0];
      if (selectedPlatform) {
        this.pages = [
          {
            social_media_id: selectedPlatform.social_media_id,
            access_token: selectedPlatform.access_token,
          },
        ];
      }

      // Load posts AFTER context is ready
      this.loadSidebarPosts();

      setTimeout(() => {
        this.loadFollowersChart();
        this.loadMetrics();
      }, 200);
    });
  }

  /** ===============================
   *  EVENTS
   *  =============================== */
  viewFollowers(view: string) {
    if (this.activeView !== "followers") {
      this.activeView = view;
      this.loadFollowersChart();
      this.loadMetrics();
      this.cdr.detectChanges();
    }
  }
  viewTrends(view: string) {
    if (this.activeView !== "trends") {
      this.activeView = view;
      this.cdr.detectChanges();
    }
  }
  viewFormat(view: string) {
    if (this.activeView !== "format") {
      this.activeView = view;
      this.cdr.detectChanges();
    }
  }
  viewAlerts(view: string) {
    if (this.activeView !== "alerts") {
      this.activeView = view;
      this.cdr.detectChanges();
    }
  }

  private extractMetric(res: any) {
    const data = res.data?.[0]?.insight?.data?.[0]?.values || [];
    if (!data.length) return { value: 0, diff: 0 };
    const first = data[0].value;
    theLast: {
      // ensure robust "last" across engines
    }
    const last = data[data.length - 1].value;
    return { value: last, diff: last - first };
  }

  getRightCards() {
    const cards = {
      followers: [
        { title: "Trends", img: "assets/screens/trends-chart.png", action: () => this.viewTrends("trends") },
        { title: "Alerts", img: "assets/screens/alerts.png", action: () => this.viewAlerts("alerts") },
      ],
      trends: [
        { title: "Overview", img: "assets/screens/overview-chart.png", action: () => this.viewFollowers("followers") },
        { title: "Alerts", img: "assets/screens/alerts.png", action: () => this.viewAlerts("alerts") },
      ],
      format: [
        { title: "Overview", img: "assets/screens/overview-chart.png", action: () => this.viewFollowers("followers") },
        { title: "Trends", img: "assets/screens/trends-chart.png", action: () => this.viewTrends("trends") },
        { title: "Alerts", img: "assets/screens/alerts.png", action: () => this.viewAlerts("alerts") },
      ],
      alerts: [
        { title: "Overview", img: "assets/screens/overview-chart.png", action: () => this.viewFollowers("followers") },
        { title: "Trends", img: "assets/screens/trends-chart.png", action: () => this.viewTrends("trends") },
      ],
    };
    return (cards as Record<string, any[]>)[this.activeView] || [];
  }

  get showDropdowns(): boolean {
    return this.activeView !== "alerts";
  }

  get currentViewTitle(): string {
    switch (this.activeView) {
      case "followers":
        return "Overview";
      case "trends":
        return "Trends";
      case "format":
        return "Format Breakdown";
      case "alerts":
        return "Alerts";
      case "toparticles":
        return "Popular Posts";
      default:
        return "Overview";
    }
  }

  onSiteChange(siteId: number): void {
    this.selectedSiteId = siteId;
    this.filteredPlatforms = this.platforms.filter(
      (p) =>
        p.site_id === siteId &&
        (p.platform.toLowerCase().includes("facebook") ||
          p.platform.toLowerCase().includes("instagram"))
    );
    this.lastChartValue = null;
    this.diffChartValue = null;

    if (this.filteredPlatforms.length > 0) {
      const firstPlatform = this.filteredPlatforms[0];
      this.selectedPlatformId = firstPlatform.id;
      this.pages = [
        {
          social_media_id: firstPlatform.social_media_id,
          access_token: firstPlatform.access_token,
        },
      ];
      switch (this.activeView) {
        case "followers":
          this.loadFollowersChart();
          this.loadMetrics();
          break;
        default:
          this.cdr.detectChanges();
          break;
      }
    } else {
      this.selectedPlatformId = null;
      this.pages = [];
    }
  }

  onPlatformChange(platformId: number): void {
    this.selectedPlatformId = platformId;
    const selectedPlatform = this.filteredPlatforms.find((p) => p.id === platformId);
    if (!selectedPlatform) return;
    this.pages = [
      {
        social_media_id: selectedPlatform.social_media_id,
        access_token: selectedPlatform.access_token,
      },
    ];
    switch (this.activeView) {
      case "followers":
        this.loadFollowersChart();
        this.loadMetrics();
        break;
      default:
        this.cdr.detectChanges();
        break;
    }
  }

  onPeriodChange(period: string): void {
    this.selectedPeriod = period;
    switch (this.activeView) {
      case "followers":
        this.loadFollowersChart();
        this.loadMetrics();
        break;
      default:
        this.cdr.detectChanges();
        break;
    }
  }

  /** ===============================
   *  LOADERS
   *  =============================== */
  private loadFollowersChart(platform?: any): void {
    if (!this.selectedSiteId || !this.selectedPlatformId) return;

    this.lastChartValue = null;
    this.diffChartValue = null;
    this.isLoaded = false;

    const selectedPlatform =
      platform || this.platforms.find((p) => p.id === this.selectedPlatformId);

    if (!selectedPlatform) return;

    const platformName = (selectedPlatform?.platform || "").toLowerCase();
    const isInstagram =
      platformName.includes("instagram") ||
      platformName.includes("insta") ||
      platformName.includes("ig");

    const periodToUse = isInstagram ? "day" : this.selectedPeriod;

    const payload = {
      content_site_id: this.selectedSiteId,
      social_platform_id: this.selectedPlatformId,
      period: periodToUse,
      pages: [
        {
          social_media_id: selectedPlatform.social_media_id,
          access_token: selectedPlatform.access_token,
        },
      ],
    };

    const endpoint = isInstagram
      ? `querymetrics/parseInstaAnalytics/${this.companyID}?metric=follower_count`
      : `querymetrics/parseFbAnalytics/${this.companyID}?metric=page_follows`;

    this.coreService.postData(endpoint, payload).subscribe((res) => {
      this.isLoaded = true;

      const meta = res.meta || res.original?.meta || {};

      // instagram
      if (isInstagram && meta.adjustedValues?.length) {
        let adjustedValues: number[] = meta.adjustedValues;
        let dates: string[] = meta.dates || [];

        if (this.selectedPeriod === "week" && adjustedValues.length > 7) {
          const startIndex = adjustedValues.length - 7;
          adjustedValues = adjustedValues.slice(startIndex);
          dates = dates.slice(startIndex);
        }

        const categories =
          dates.length === adjustedValues.length
            ? dates.map((d) => {
                const date = new Date(d.replace("+0000", "Z"));
                date.setDate(date.getDate() + 1);
                return date.toLocaleDateString("el-GR", {
                  day: "2-digit",
                  month: "2-digit",
                });
              })
            : adjustedValues.map((_, i) => `Day ${i + 1}`);

        this.chartOptions6 = this.createChartOptions(adjustedValues, categories);

        const lastValue = adjustedValues[adjustedValues.length - 1] ?? 0;
        const firstValue = adjustedValues[0] ?? 0;
        this.lastChartValue = Number(meta.todayFollowers || lastValue);
        this.diffChartValue = lastValue - firstValue;

        this.cdr.detectChanges();
        return;
      }

      // facebook
      const data = res.original?.data || res.data || [];
      if (!data.length) {
        this.chartOptions6 = this.createChartOptions([], []);
        this.cdr.detectChanges();
        return;
      }

      const values = data[0]?.insight?.data?.[0]?.values || [];
      if (!values.length) {
        this.chartOptions6 = this.createChartOptions([], []);
        this.cdr.detectChanges();
        return;
      }

      const chartData = values.map((v: any) => ({
        end_time: v.end_time,
        value: v.value,
      }));

      this.chartOptions6 = this.prepareChart(chartData);

      const lastValue = chartData[chartData.length - 1]?.value ?? 0;
      const firstValue = chartData[0]?.value ?? 0;
      this.lastChartValue = lastValue;
      this.diffChartValue = lastValue - firstValue;

      this.cdr.detectChanges();
    });
  }

  /** ===============================
   *  CHART HELPERS
   *  =============================== */
  private prepareChart(data: any[]): ChartOptions {
    if (!data?.length) return this.createChartOptions([], []);
    if (this.selectedPeriod === "week" && data.length > 7) {
      data = data.slice(-7);
    }
    const categories = data.map((d) =>
      new Date(d.end_time).toLocaleDateString("el-GR", {
        day: "2-digit",
        month: "2-digit",
      })
    );
    const values = data.map((d) => d.value);
    return this.createChartOptions(values, categories);
  }

  private createChartOptions(
    data: number[],
    categories: (string | number)[]
  ): ChartOptions {
    return {
      series: [{ data }],
      chart: {
        type: "area",
        height: 360,
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      stroke: { curve: "smooth", width: 2, colors: ["#6ab7ff"] },
      dataLabels: { enabled: false },
      grid: {
        borderColor: "#f1f3f4",
        strokeDashArray: 0,
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } },
      },
      yaxis: {
        tickAmount: 5,
        labels: {
          style: { colors: "#777", fontSize: "12px" },
          formatter: (val: number, index: number) =>
            index === 0 ? "" : val.toLocaleString(),
        },
      },
      xaxis: {
        categories,
        tickAmount: 6,
        labels: { style: { colors: "#777", fontSize: "12px" } },
      },
      tooltip: {
        theme: "light",
        y: { formatter: (val: number) => val.toLocaleString("el-GR") },
      },
      legend: {
        show: true,
        position: "bottom",
        horizontalAlign: "center",
        labels: { colors: "#888" },
      },
      title: {
        align: "left",
        style: { fontSize: "15px", fontWeight: "600", color: "#333" },
      },
    };
  }

  /** ===============================
   *  API HELPERS
   *  =============================== */
  loadSites(): any {
    return this.coreService.getData(`/sites-with-feeds/${this.companyID}`).pipe(
      take(1),
      map((res: any) => (res.data || []).filter((site: any) => site.hasFeed)),
      catchError(() => of([]))
    );
  }

  loadPlatforms(): any {
    return this.coreService.getData("/getSocialPlatforms").pipe(
      take(1),
      map((res: any) =>
        (res.data as SocialPlatformI[]).filter(
          (p) =>
            p.isApproved === "Approved" &&
            !!p.access_token &&
            p.platform !== "Instagram"
        )
      ),
      catchError(() => of([]))
    );
  }

  loadMetrics(): void {
    if (!this.selectedSiteId || !this.selectedPlatformId) return;
    this.isMetricsLoaded = false;

    const selectedPlatform = this.filteredPlatforms.find(
      (p) => p.id === this.selectedPlatformId
    );
    const isInstagram = selectedPlatform?.platform
      ?.toLowerCase()
      .includes("instagram");

    const payload = {
      content_site_id: this.selectedSiteId,
      social_platform_id: this.selectedPlatformId,
      period: this.selectedPeriod,
      pages: this.pages,
    };

    if (isInstagram) {
      this.metrics.reach = { value: 0, diff: 0 };
      this.metrics.accountImpressions = { value: 0, diff: 0 };
      this.metrics.profileImpressions = { value: 0, diff: 0 };

      if (this.lastChartValue !== null && this.diffChartValue !== null) {
        this.isMetricsLoaded = true;
        this.cdr.detectChanges();
        return;
      }

      setTimeout(() => {
        this.isMetricsLoaded = true;
        this.cdr.detectChanges();
      }, 500);

      return;
    }

    const reach$ = this.coreService.postData(
      `querymetrics/parseFbAnalytics/${this.companyID}?metric=page_total_media_view_unique`,
      payload
    );
    const accountImpressions$ = this.coreService.postData(
      `querymetrics/parseFbAnalytics/${this.companyID}?metric=page_impressions`,
      payload
    );
    const profileImpressions$ = this.coreService.postData(
      `querymetrics/parseFbAnalytics/${this.companyID}?metric=page_views_total`,
      payload
    );

    forkJoin({
      reach: reach$,
      accountImpressions: accountImpressions$,
      profileImpressions: profileImpressions$,
    }).subscribe(({ reach, accountImpressions, profileImpressions }) => {
      this.metrics.reach = this.extractMetric(reach);
      this.metrics.accountImpressions = this.extractMetric(accountImpressions);
      this.metrics.profileImpressions = this.extractMetric(profileImpressions);
      this.isMetricsLoaded = true;
      this.cdr.detectChanges();
    });
  }

  /** ========== Sidebar posts loader + helpers ========== */
  private loadSidebarPosts(): void {
    if (typeof window === 'undefined') {
      this.posts = [];
      return;
    }

    const raw = localStorage.getItem(this.LATEST_POSTS_KEY);

    if (!raw) {
      this.posts = [];
      this.cdr.detectChanges();
      return;
    }

    try {
      const parsed = JSON.parse(raw) as Post[] | null;

      if (Array.isArray(parsed)) {
        this.posts = parsed.slice(0, this.maxSidebarCards);
      } else {
        this.posts = [];
      }
    } catch (e) {
      console.error('Failed to parse latest posts from localStorage', e);
      this.posts = [];
    }

    this.cdr.detectChanges();
  }


  platformTextClass(plat?: string) {
    const t = (plat || "").toLowerCase().trim();
    switch (t) {
      case "instagram":
        return "text-fuchsia-600";
      case "facebook":
        return "text-blue-600";
      case "x":
        return "text-neutral-700";
      case "youtube":
        return "text-red-600";
      default:
        return "text-gray-700";
    }
  }

  onMainImgError(ev: Event): void {
    const img = ev.target as HTMLImageElement;
    if (!img || img.src.endsWith(this.naPlaceholder)) return;
    img.src = this.naPlaceholder;
  }

  viewRow(row: Post) {
    const targetId =
      row.enable_repeat === -1 ? row.parent_post_id ?? row.id : row.id;
    this.router.navigate(["/preview-post", row.tag, targetId]);
  }

  trackById = (_: number, item: Post) => item.id;
}
